// useTimelineEntries.js

import { useEffect, useMemo, useState } from 'react';

import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { normalizeDateValue } from 'utilities/DateTime';
import { getReadableForegroundColor } from 'utilities/Colors';
import { getEnumOptionColor } from 'utilities/EnumOptions';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { getPeopleProfiles } from 'views/utils/peopleFieldProfiles';
import { listHierarchyParentsForNodeKeys } from 'services/nodes/NodeEdgeService';
import { buildTimelineHierarchyEntries } from 'views/timeline/utils/buildTimelineHierarchy';
import { sortTimelineEntriesByStart } from 'views/timeline/utils/sortTimelineEntriesByStart';
import { resolveTaskPredecessorLinks } from 'schedule/dependencies/predecessorLinks';

/**
 * Prefer the node identity color (schema `color` field → root `node.color`),
 * then a resolved schema color value, then grouped enum colors.
 *
 * @param {object} node
 * @param {string} [groupColor]
 * @param {string} [schemaColor]
 * @returns {{ barColor: string, barForeground: string }}
 */
const resolveTimelineBarColors = (node, groupColor = '', schemaColor = '') => {
  const identity = typeof node?.color === 'string' ? node.color.trim() : '';
  const fromSchema = typeof schemaColor === 'string' ? schemaColor.trim() : '';
  const fromGroup = typeof groupColor === 'string' ? groupColor.trim() : '';
  const barColor = identity || fromSchema || fromGroup || '';
  return {
    barColor,
    barForeground: barColor ? getReadableForegroundColor(barColor) : '',
  };
};

const parseTemporalRange = (value, fieldType) => {
  const normalizedType = String(fieldType || '').toLowerCase();

  if (value == null) return null;

  let startCandidate = null;
  let endCandidate = null;

  if (typeof value === 'object') {
    startCandidate = value?.startDate ?? value?.start ?? value?.from ?? value?.date ?? value?.dateTime;
    endCandidate = value?.endDate ?? value?.end ?? value?.to ?? null;
  } else {
    startCandidate = value;
  }

  if (normalizedType === 'date' || normalizedType === 'datetime') {
    const point = normalizeDateValue(startCandidate ?? value);
    if (!point) return null;
    return {
      start: point,
      end: point,
      isMilestone: true,
    };
  }

  const start = normalizeDateValue(startCandidate);
  if (!start) return null;

  const parsedEnd = normalizeDateValue(endCandidate);
  const end = parsedEnd && parsedEnd.getTime() >= start.getTime()
    ? parsedEnd
    : start;

  return {
    start,
    end,
    isMilestone: end.getTime() === start.getTime(),
  };
};

const useTimelineEntries = ({ view, typeDef, nodes = [] }) => {
  const timelineField = view?.timelineField || '';
  const timelineRowMode = view?.timelineRowMode || 'flat';
  // Legacy "hierarchy" row mode → flat listing + hierarchy flag
  const effectiveTimelineRowMode = timelineRowMode === 'hierarchy' ? 'flat' : timelineRowMode;
  const timelineGroupField = view?.timelineGroupField || '';
  const showUnscheduled = view?.showUnscheduled == null ? true : Boolean(view?.showUnscheduled);
  const preset = String(view?.preset || 'timeline').trim().toLowerCase() === 'gantt'
    ? 'gantt'
    : 'timeline';
  const showHierarchy = view?.showHierarchy != null
    ? Boolean(view.showHierarchy)
    : (timelineRowMode === 'hierarchy' || preset === 'gantt');
  const showDependencies = view?.showDependencies != null
    ? Boolean(view.showDependencies)
    : preset === 'gantt';
  const allowScheduleEdit = Boolean(view?.allowScheduleEdit);

  const [parentByChild, setParentByChild] = useState({});

  const schemaFields = useMemo(() => (
    resolveDisplaySchemaFields(typeDef, { includeHidden: true })
  ), [typeDef]);

  const predecessorsFieldDef = useMemo(() => (
    schemaFields.find((field) => {
      const key = String(field?.name || field?.key || '').trim();
      const type = String(field?.type || '').toLowerCase();
      if (key === 'predecessorLinks') return true;
      return key === 'predecessors' && isRelationFieldType(type);
    }) || null
  ), [schemaFields]);

  const hasDependencyField = Boolean(predecessorsFieldDef) || schemaFields.some((field) => {
    const key = String(field?.name || field?.key || '').trim();
    return key === 'predecessors' || key === 'predecessorLinks';
  });

  const baseKey = useMemo(() => {
    const fromNodes = (Array.isArray(nodes) ? nodes : [])
      .map((node) => String(node?.baseKey || '').trim())
      .find(Boolean);
    return fromNodes || null;
  }, [nodes]);

  useEffect(() => {
    if (!showHierarchy) {
      setParentByChild({});
      return undefined;
    }

    const keys = (Array.isArray(nodes) ? nodes : [])
      .map((node) => String(node?.key || '').trim())
      .filter(Boolean);
    if (!keys.length || !baseKey) {
      setParentByChild({});
      return undefined;
    }

    let cancelled = false;
    listHierarchyParentsForNodeKeys(keys, { baseKey })
      .then((map) => {
        if (!cancelled) setParentByChild(map || {});
      })
      .catch(() => {
        if (!cancelled) setParentByChild({});
      });

    return () => {
      cancelled = true;
    };
  }, [showHierarchy, nodes, baseKey]);

  const timelineFieldDef = useMemo(() => {
    if (!timelineField) return null;
    return schemaFields.find((field) => {
      const fieldKey = field?.name || field?.key;
      return fieldKey && fieldKey === timelineField;
    }) || null;
  }, [schemaFields, timelineField]);

  const schemaFieldMap = useMemo(() => {
    const map = new Map();
    (schemaFields || []).forEach((field) => {
      const key = field?.name || field?.key;
      if (key) map.set(key, field);
    });
    return map;
  }, [schemaFields]);

  const groupFieldDef = useMemo(() => {
    if (!timelineGroupField) return null;
    return schemaFieldMap.get(timelineGroupField) || null;
  }, [schemaFieldMap, timelineGroupField]);

  const colorFieldDef = useMemo(() => (
    schemaFields.find((field) => {
      const key = String(field?.name || field?.key || '').trim().toLowerCase();
      const type = String(field?.type || '').toLowerCase();
      return type === 'color' || key === 'color';
    }) || null
  ), [schemaFields]);

  const parsedEntries = useMemo(() => {
    const safeNodes = Array.isArray(nodes) ? nodes : [];

    const buildGroupTokens = (rawGroupValue) => {
      if (!timelineGroupField || effectiveTimelineRowMode !== 'grouped' || !groupFieldDef) return [];

      const groupFieldType = String(groupFieldDef?.type || '').toLowerCase();

      if (groupFieldType === 'people') {
        const profiles = getPeopleProfiles(rawGroupValue);
        if (!profiles.length) {
          return [{ key: '__UNGROUPED__', kind: 'ungrouped', label: 'Ungrouped' }];
        }

        return profiles.map((profile, index) => {
          const rawKey = String(profile?.key || '').trim();
          const fallbackKey = rawKey || `person_${index}`;
          const rawLabel = String(profile?.name || '').trim();
          return {
            key: `person:${fallbackKey}`,
            kind: 'people',
            label: rawLabel || '',
            personKey: fallbackKey,
          };
        });
      }

      const normalizedValue = rawGroupValue == null ? '' : String(rawGroupValue).trim();
      if (!normalizedValue) {
        return [{ key: '__UNGROUPED__', kind: 'ungrouped', label: 'Ungrouped' }];
      }

      if (groupFieldType === 'enum') {
        return [{
          key: `enum:${normalizedValue}`,
          kind: 'enum',
          enumValue: normalizedValue,
        }];
      }

      return [{
        key: `value:${normalizedValue}`,
        kind: 'value',
        label: normalizedValue,
      }];
    };

    if (!timelineFieldDef) {
      return safeNodes.map((node) => ({
        node,
        key: node?.key || '',
        title: node?.title || 'Untitled',
        range: null,
        groupValue: null,
        groupTokens: [],
        depth: 0,
      }));
    }

    return safeNodes.map((node) => {
      const rawValue = resolveSchemaFieldValue({
        item: node,
        fieldDef: timelineFieldDef,
        typeKey: typeDef?.key,
        dataSection: typeDef?.dataSection,
      });

      const rawGroupValue = groupFieldDef
        ? resolveSchemaFieldValue({
          item: node,
          fieldDef: groupFieldDef,
          typeKey: typeDef?.key,
          dataSection: typeDef?.dataSection,
        })
        : null;

      const groupColor = effectiveTimelineRowMode === 'grouped' && groupFieldDef?.options && rawGroupValue != null
        ? getEnumOptionColor(groupFieldDef.options, String(rawGroupValue))
        : '';

      const rawColorValue = colorFieldDef
        ? resolveSchemaFieldValue({
          item: node,
          fieldDef: colorFieldDef,
          typeKey: typeDef?.key,
          dataSection: typeDef?.dataSection,
        })
        : null;
      const schemaColor = typeof rawColorValue === 'string'
        ? rawColorValue
        : (typeof rawColorValue?.color === 'string' ? rawColorValue.color : '');

      const groupTokens = buildGroupTokens(rawGroupValue);
      const nodeKey = node?.key || '';
      const predecessorLinks = hasDependencyField
        ? resolveTaskPredecessorLinks(node, {
          typeKey: typeDef?.key,
          dataSection: typeDef?.dataSection,
        })
        : [];
      const predecessorKeys = predecessorLinks.map((link) => link.taskKey);
      const { barColor, barForeground } = resolveTimelineBarColors(node, groupColor, schemaColor);

      return {
        node,
        key: nodeKey,
        title: node?.title || 'Untitled',
        range: parseTemporalRange(rawValue, timelineFieldDef?.type),
        groupValue: rawGroupValue,
        groupTokens,
        barColor,
        barForeground,
        parentKey: parentByChild[nodeKey] || null,
        predecessorKeys,
        predecessorLinks,
        depth: 0,
      };
    });
  }, [
    nodes,
    timelineFieldDef,
    groupFieldDef,
    colorFieldDef,
    effectiveTimelineRowMode,
    timelineGroupField,
    typeDef?.key,
    typeDef?.dataSection,
    parentByChild,
    hasDependencyField,
  ]);

  const scheduledEntries = useMemo(() => {
    const scheduled = parsedEntries.filter((entry) => entry?.range?.start && entry?.range?.end);
    if (!showHierarchy) {
      return sortTimelineEntriesByStart(scheduled);
    }
    return buildTimelineHierarchyEntries(scheduled, parentByChild);
  }, [parsedEntries, showHierarchy, parentByChild]);

  const unscheduledEntries = useMemo(() => {
    const unscheduled = parsedEntries.filter((entry) => !entry?.range);
    if (!showHierarchy) {
      return sortTimelineEntriesByStart(unscheduled);
    }
    return buildTimelineHierarchyEntries(unscheduled, parentByChild);
  }, [parsedEntries, showHierarchy, parentByChild]);

  return {
    timelineField,
    timelineFieldDef,
    groupFieldDef,
    effectiveTimelineRowMode,
    showUnscheduled,
    showHierarchy,
    showDependencies: Boolean(showDependencies && hasDependencyField),
    allowScheduleEdit,
    preset,
    predecessorsFieldDef,
    scheduledEntries,
    unscheduledEntries,
  };
};

export default useTimelineEntries;
